import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/page-hero";
import { CtaBlock } from "@/components/cta";
import { Reveal } from "@/lib/motion";
import { METODO } from "@/content/site";
import { IMAGES } from "@/content/images";

export default function RutaLegal() {
  return (
    <>
      <Seo
        title="Ruta legal personal"
        description="La ruta legal personal de Tulevik: un recorrido claro desde tu momento de vida hasta la decisión jurídica correcta, sin automatismos ni promesas vacías."
        path="/ruta-legal"
      />
      <PageHero
        eyebrow="El concepto"
        titulo="¿Qué es la ruta legal personal?"
        lead="Es la idea que ordena todo lo que hacemos: tu asunto no empieza en un código legal, empieza en tu vida. Primero ubicamos el momento; después elegimos la herramienta jurídica correcta —no al revés."
      />

      <section className="bg-parchment">
        <div className="mx-auto grid max-w-7xl items-start gap-12 px-4 py-16 sm:px-8 sm:py-24 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-serif text-2xl text-ink-deep sm:text-3xl">Así recorremos la ruta, contigo</h2>
            </Reveal>
            <div className="mt-10 space-y-0">
              {METODO.map((fase, i) => (
                <Reveal key={fase.n} delay={i * 0.06} y={16}>
                  <div className="flex gap-6 border-t border-hairline py-8 last:border-b" data-testid={`ruta-fase-${fase.n}`}>
                    <span className="font-mono text-sm text-azul">{fase.n}</span>
                    <div>
                      <h3 className="font-serif text-xl text-ink-deep sm:text-2xl">{fase.titulo}</h3>
                      <p className="mt-2 max-w-xl leading-relaxed text-ink-muted">{fase.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal className="mt-14">
              <div className="border-l-2 border-azul bg-azul-light/40 p-6 sm:p-8">
                <h3 className="font-serif text-xl text-ink-deep">¿Para quién funciona mejor?</h3>
                <ul className="mt-4 space-y-2.5 text-sm leading-relaxed text-ink-text">
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />Personas con patrimonio, vivienda o conflictos civiles</li>
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />Personas con negocio o en proceso de emprender</li>
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />Empleadores o trabajadores con una necesidad laboral inicial</li>
                  <li className="flex gap-3"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-azul" />Familias que necesitan ordenar acuerdos o decisiones</li>
                </ul>
              </div>
            </Reveal>
          </div>

          <aside className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="border border-hairline bg-parchment-surface p-2.5 lg:sticky lg:top-24">
                <img
                  src={IMAGES.ventanaNatural}
                  alt="Ventana arquitectónica con luz natural"
                  className="h-[320px] w-full object-cover sm:h-[420px]"
                  loading="lazy"
                />
                <p className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-azul">
                  Primero la vida, después el trámite
                </p>
              </div>
              <p className="mt-8 text-sm leading-relaxed text-ink-muted">
                La ruta legal personal no es un producto: es una forma de trabajar. No hay formularios
                automáticos que decidan por ti ni diagnósticos hechos por software. Hay una persona que
                escucha, clasifica con criterio y te dice la verdad sobre tu asunto.
              </p>
            </Reveal>
          </aside>
        </div>
      </section>

      <CtaBlock
        titulo="¿Quieres encontrar tu ruta?"
        sub="Puedes empezar por el orientador —tres preguntas, cero registro— o contarnos directo tu situación."
      />
    </>
  );
}
